// Чистые функции блока «Цели и питание»: цели из отчёта и сдвиг ряда Zepp на поправку.

import type { SeriesPoint } from '@/components/charts/TimeSeriesChart'

import type { Calibration, InBodyPoint, ScanOut } from '../api'

export type GoalSuggestion = {
  kind: 'fat_mass_kg' | 'body_fat_pct'
  target: number
  label: string
  hint: string
}

const round1 = (value: number) => Math.round(value * 10) / 10
const comma = (value: number) => String(value).replace('.', ',').replace('-', '−')

function fatKg(scan: ScanOut): number | null {
  if (scan.body_fat_kg != null) return scan.body_fat_kg
  if (scan.body_fat_pct != null) return (scan.weight_kg * scan.body_fat_pct) / 100
  return null
}

/** Предложения целей из отчёта: «до идеального: жир» (кг) и верхняя граница нормы жира (%). */
export function goalSuggestions(latest: ScanOut | null): GoalSuggestion[] {
  if (!latest) return []
  const result: GoalSuggestion[] = []
  const current = fatKg(latest)
  const toIdeal = latest.to_ideal_fat_kg ?? null
  if (current !== null && toIdeal !== null && toIdeal < 0) {
    const target = round1(current + toIdeal)
    result.push({
      kind: 'fat_mass_kg',
      target,
      label: 'Жир, кг',
      hint: `до идеального: жир ${comma(toIdeal)} кг, цель ${comma(target)} кг`,
    })
  }
  const norm = latest.norms?.body_fat_pct
  if (norm && latest.body_fat_pct != null && latest.body_fat_pct > norm.high) {
    result.push({
      kind: 'body_fat_pct',
      target: norm.high,
      label: 'Жир, %',
      hint: `до верхней границы нормы: ${comma(norm.high)} %`,
    })
  }
  return result
}

export function goalLink(suggestion: GoalSuggestion): string {
  const params = new URLSearchParams({ new: '1', kind: suggestion.kind, target: String(suggestion.target) })
  return `/teloscope/goals?${params.toString()}`
}

/** Поправка для метрики; null — пар нет, переключатель не показываем. */
export function offsetFor(calibration: Calibration[] | undefined, metric: Calibration['metric']): number | null {
  const found = Array.isArray(calibration) ? calibration.find((c) => c.metric === metric) : undefined
  return found && found.offset !== null && found.pairs > 0 ? found.offset : null
}

/** Ряд Zepp с поправкой: из каждого значения вычитается среднее расхождение (Zepp − InBody). */
export function shiftPoints<T extends { y: number | null }>(points: T[], offset: number | null): T[] {
  if (offset === null) return points
  return points.map((p) => (p.y === null ? p : { ...p, y: Math.round((p.y - offset) * 100) / 100 }))
}

/** Ступени суточной нормы калорий: с даты полного замера действует его норма до следующего.
 * `latest` заменяет значение последней ступени (норма с дефицитом при цели на снижение). */
export function kcalNormSteps(points: InBodyPoint[] | undefined, latest: number | null = null): SeriesPoint[] {
  const steps = (points ?? [])
    .filter((p) => p.complete)
    .sort((a, b) => a.day.localeCompare(b.day))
    .map((p) => ({ x: p.day, y: p.value }))
  const last = steps[steps.length - 1]
  if (last && latest !== null) last.y = latest
  return steps
}
